import { Button, Checkbox, ClipboardButton, Dialog, Select, TextInput, Typography, toast } from '@neo4j-ndl/react';
import { fleetManagementApi, useActiveProjectSummary } from '@nx/state';
import type { Token } from '@nx/state';
import { useState } from 'react';

import { ApiErrorBanner } from '../../shared/components';
import { useTokenExpiryTimeOptions } from '../hooks/use-token-expiry-time-options';
import type { TokenExpiryOption } from '../shared/types';

export const isValidTokenName = (name: string | undefined) =>
  name !== undefined && name.length >= 3 && name.length <= 30;

export const tokenNameValidator = (name: string | undefined) => {
  return isValidTokenName(name) ? undefined : (
    <div>
      <Typography variant="body-medium">Name should match the requirements</Typography>
      <ul className="list-disc">
        <li>
          <Typography variant="body-small">Minimum 3 characters to max 30 characters</Typography>
        </li>
      </ul>
    </div>
  );
};

export const TokenNameInput = ({
  name,
  setName,
  isReadOnly,
}: {
  name: string | undefined;
  setName: (name: string | undefined) => void;
  isReadOnly: boolean;
}) => {
  return (
    <TextInput
      isFluid
      size="medium"
      onChange={(e) => setName(e.target.value)}
      label="Name"
      isReadOnly={isReadOnly}
      errorText={(name ?? '') && tokenNameValidator(name)}
      isRequired={true}
      helpText={
        'Name to identify the token. This token name will be used as the default name of the deployment registered with the token.'
      }
    />
  );
};

export const TokenExpirySelection = ({
  expiry,
  setSelectedExpiry,
}: {
  setSelectedExpiry: (expiry: TokenExpiryOption) => void;
  expiry: TokenExpiryOption | undefined;
}) => {
  const expiryTimeOptions = useTokenExpiryTimeOptions();
  return (
    <Select
      label={<Typography variant="body-medium">Expiry</Typography>}
      type="select"
      size="medium"
      selectProps={{
        onChange: (option) => {
          if (option !== null) {
            setSelectedExpiry(option);
          }
        },
        isMulti: false,
        value: expiry,
        options: expiryTimeOptions,
        menuPosition: 'fixed',
      }}
    />
  );
};

export const TokenAutoRotationOption = ({
  autoRotate,
  setAutoRotate,
}: {
  autoRotate: boolean;
  setAutoRotate: (autoRotate: boolean) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Checkbox label="Auto-rotate token" isChecked={autoRotate} onChange={(e) => setAutoRotate(e.target.checked)} />
      <Typography variant="body-small" className="ndl-form-message">
        The token will be automatically rotated after it expires. This ensures that a valid token is always available
        for the plugin to report.
      </Typography>
    </div>
  );
};

export const TokenOutput = ({ token }: { token: string }) => {
  return (
    <TextInput
      isFluid
      value={token}
      label="Token credential"
      htmlAttributes={{
        'aria-label': 'Token credential',
        readOnly: true,
      }}
      rightElement={
        <ClipboardButton
          textToCopy={token}
          htmlAttributes={{ 'aria-label': 'Copy to clipboard' }}
          tooltipProps={{
            isPortaled: false,
            type: 'simple',
          }}
        />
      }
    />
  );
};

const CreateTokenModal = ({ isOpen, closeModal }: { isOpen: boolean; closeModal: () => void }) => {
  const activeProjectSummary = useActiveProjectSummary();
  const [createToken, { error, isLoading }] = fleetManagementApi.useCreateTokenMutation();
  const [token, setToken] = useState<string>();
  const [autoRotate, setAutoRotate] = useState(true);

  const [name, setName] = useState<string | undefined>();
  const [expiry, setSelectedExpiry] = useState<TokenExpiryOption | undefined>();

  const onClose = () => {
    setToken(undefined);
    closeModal();
  };

  return (
    <Dialog size="small" isOpen={isOpen} onClose={onClose}>
      <Dialog.Header>{token !== undefined ? 'Token created' : 'Create token'}</Dialog.Header>
      <Dialog.Content className="flex flex-col gap-7">
        {error && <ApiErrorBanner error={error} />}
        {!error && (
          <>
            <TokenNameInput name={name} setName={setName} isReadOnly={token !== undefined} />

            {token !== undefined && expiry !== undefined ? (
              <TextInput isReadOnly={true} value={expiry.label} label="Token expiry" />
            ) : (
              <div className="flex flex-col gap-2">
                <TokenExpirySelection expiry={expiry} setSelectedExpiry={setSelectedExpiry} />
                <TokenAutoRotationOption autoRotate={autoRotate} setAutoRotate={setAutoRotate} />
              </div>
            )}
            {token !== undefined && <TokenOutput token={token} />}
          </>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Button fill="outlined" color="neutral" onClick={onClose}>
          {token !== undefined ? 'Done' : 'Cancel'}
        </Button>
        {token === undefined && (
          <Button
            fill="filled"
            color="primary"
            onClick={() => {
              void createToken({
                project_id: activeProjectSummary?.id ?? '',
                rotation_interval: expiry?.label ?? '',
                name: name ?? '',
                auto_rotate: autoRotate,
              })
                .then((res) => {
                  if (res.data !== undefined && res.data.token !== '') {
                    toast.success('Token created', { shouldAutoClose: true });
                    setToken(res.data.token);
                  }
                })
                .catch((err: Error) => {
                  toast.danger(err.message, { shouldAutoClose: true });
                });
            }}
            isLoading={isLoading}
            isDisabled={!isValidTokenName(name) || expiry === undefined}
          >
            Create
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
};

export const RegenerateTokenModal = ({
  isOpen,
  closeModal,
  tokenInContext,
}: {
  isOpen: boolean;
  closeModal: () => void;
  tokenInContext: Token;
}) => {
  const activeProjectSummary = useActiveProjectSummary();
  const [regenerateToken, { error, isLoading }] = fleetManagementApi.useRegenerateTokenMutation();
  const [token, setToken] = useState<string>();
  const [autoRotate, setAutoRotate] = useState(true);
  const [expiry, setSelectedExpiry] = useState<TokenExpiryOption | undefined>();

  const onClose = () => {
    setToken(undefined);
    closeModal();
  };

  return (
    <Dialog size="small" isOpen={isOpen} onClose={onClose}>
      <Dialog.Header>{token !== undefined ? 'Token regenerated' : 'Regenerate token'}</Dialog.Header>
      <Dialog.Content className="flex flex-col gap-7">
        {error && <ApiErrorBanner error={error} />}
        <TextInput isFluid size="medium" value={tokenInContext.name} label="Name" isReadOnly={true} />
        {token !== undefined && expiry !== undefined ? (
          <TextInput isReadOnly={true} value={expiry.label} label="Token expiry" />
        ) : (
          <div className="flex flex-col gap-2">
            <TokenExpirySelection expiry={expiry} setSelectedExpiry={setSelectedExpiry} />
            <TokenAutoRotationOption autoRotate={autoRotate} setAutoRotate={setAutoRotate} />
          </div>
        )}
        {token !== undefined && <TokenOutput token={token} />}
      </Dialog.Content>
      <Dialog.Actions>
        <Button fill="outlined" color="neutral" onClick={onClose}>
          {token !== undefined ? 'Done' : 'Cancel'}
        </Button>
        {token === undefined && (
          <Button
            fill="filled"
            color="primary"
            onClick={() => {
              void regenerateToken({
                projectId: activeProjectSummary?.id ?? '',
                req: {
                  new_rotation_interval: expiry?.label ?? '',
                  auto_rotate: autoRotate,
                },
                tokenId: tokenInContext.id,
              })
                .then((res) => {
                  if (res.data !== undefined && res.data.token !== '') {
                    toast.success('Token regenrated', { shouldAutoClose: true });
                    setToken(res.data.token);
                  }
                })
                .catch((err: Error) => {
                  toast.danger(err.message, { shouldAutoClose: true });
                });
            }}
            isLoading={isLoading}
            isDisabled={expiry === undefined}
          >
            Regenerate
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
};

export default CreateTokenModal;
